import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Sparkles, CheckCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { quizQuestions, getRecommendations, ServiceRecommendation } from "@/data/serviceFinder";

interface ServiceFinderProps {
  isOpen: boolean;
  onClose: () => void;
}

const ServiceFinder = ({ isOpen, onClose }: ServiceFinderProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [recommendations, setRecommendations] = useState<ServiceRecommendation[] | null>(null);

  const currentQuestion = quizQuestions[currentStep];
  const isLastQuestion = currentStep === quizQuestions.length - 1;
  const progress = ((currentStep + 1) / quizQuestions.length) * 100;

  const handleOptionSelect = (optionId: string) => {
    const newAnswers = { ...answers, [currentQuestion.id]: optionId };
    setAnswers(newAnswers);

    if (isLastQuestion) {
      // Calculate recommendations
      const recs = getRecommendations(newAnswers);
      setRecommendations(recs);
    } else {
      // Move to next question
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (recommendations) {
      setRecommendations(null);
    } else if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setAnswers({});
    setRecommendations(null);
  };

  const handleClose = () => {
    onClose();
    // Reset state after dialog closes
    setTimeout(handleReset, 300);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border bg-accent/5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-accent/10 rounded-lg">
              <Sparkles className="h-5 w-5 text-accent" />
            </div>
            <div>
              <DialogTitle className="text-left">Find Your Perfect Service</DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {recommendations ? "Your personalized recommendations" : "Answer 3 quick questions"}
              </p>
            </div>
          </div>
        </DialogHeader>

        {/* Progress Bar */}
        {!recommendations && (
          <div className="h-1 bg-muted">
            <div
              className="h-full bg-accent transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        <div className="px-6 py-6">
          {!recommendations ? (
            /* Questions */
            <div className="space-y-6">
              <div className="text-center">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Question {currentStep + 1} of {quizQuestions.length}
                </span>
                <h3 className="text-lg font-semibold text-foreground mt-2">
                  {currentQuestion.question}
                </h3>
              </div>

              <div className="space-y-3">
                {currentQuestion.options.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => handleOptionSelect(option.id)}
                    className={cn(
                      "w-full text-left p-4 rounded-xl border transition-all duration-200",
                      "hover:border-accent hover:bg-accent/5 hover:shadow-md",
                      "focus:outline-none focus:ring-2 focus:ring-accent/50",
                      answers[currentQuestion.id] === option.id
                        ? "border-accent bg-accent/10"
                        : "border-border bg-card"
                    )}
                  >
                    <span className="font-medium text-foreground">{option.label}</span>
                    {option.description && (
                      <p className="text-sm text-muted-foreground mt-1">{option.description}</p>
                    )}
                  </button>
                ))}
              </div>

              {currentStep > 0 && (
                <Button variant="ghost" onClick={handleBack} className="w-full">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              )}
            </div>
          ) : (
            /* Results */
            <div className="space-y-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-accent/10 rounded-full mb-3">
                  <CheckCircle className="h-6 w-6 text-accent" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">
                  Based on your answers, we recommend:
                </h3>
              </div>

              <div className="space-y-4">
                {recommendations.map((rec, index) => (
                  <div
                    key={rec.serviceId}
                    className={cn(
                      "p-4 rounded-xl border",
                      rec.priority === "primary"
                        ? "border-accent bg-accent/5"
                        : "border-border bg-card"
                    )}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {rec.priority === "primary" && (
                            <span className="text-xs font-medium text-accent bg-accent/10 px-2 py-0.5 rounded-full">
                              Best Match
                            </span>
                          )}
                          <h4 className="font-semibold text-foreground">{rec.title}</h4>
                        </div>
                        <p className="text-sm text-muted-foreground">{rec.description}</p>
                      </div>
                    </div>
                    {rec.serviceId !== "consultation" && (
                      <Link
                        to={`/services/${rec.serviceId}`}
                        onClick={handleClose}
                        className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline mt-3"
                      >
                        Learn more
                        <ArrowRight className="h-3 w-3" />
                      </Link>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-3 pt-4 border-t border-border">
                <Button asChild className="w-full group">
                  <Link to="/contact" onClick={handleClose}>
                    Get Started - Book Free Consultation
                    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
                <Button variant="outline" onClick={handleReset} className="w-full">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Start Over
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceFinder;
